/**
 * Web-specific Feature Flags Hook
 *
 * Uses window.posthog (browser SDK) instead of posthog-react-native.
 * The posthog-react-native package is not compatible with React Native Web.
 *
 * All 27 composed hooks are provided by the shared factory module.
 */

import { useState, useEffect, useCallback } from 'react';
import { createFeatureFlagHooks } from './useFeatureFlags.shared';
import type { SignupFlowVariant } from './useFeatureFlags.shared';

export type { SignupFlowVariant };

/**
 * Generic feature flag hook for web (window.posthog).
 *
 * Matches the same contract as useFeatureFlag in useFeatureFlags.ts (native)
 * so components can import from the platform-resolved useFeatureFlags path
 * without branching on platform.
 *
 * Returns undefined while PostHog is loading, boolean/string once ready.
 * Falls back gracefully to undefined if PostHog fails to initialize.
 *
 * @param flagKey - PostHog feature flag key (e.g., 'dd-feat-shared-links')
 */
export function useFeatureFlag(flagKey: string): boolean | string | undefined {
  const [value, setValue] = useState<boolean | string | undefined>(undefined);

  const evaluateFlag = useCallback(() => {
    if (typeof window === 'undefined' || !window.posthog) return;
    try {
      const raw = window.posthog.getFeatureFlag(flagKey);
      setValue(raw === null ? undefined : (raw as boolean | string | undefined));
    } catch {
      // best-effort — leave undefined
    }
  }, [flagKey]);

  useEffect(() => {
    evaluateFlag();

    if (typeof window !== 'undefined' && window.posthog?.onFeatureFlags) {
      const unsubscribe = window.posthog.onFeatureFlags(evaluateFlag);
      return typeof unsubscribe === 'function' ? unsubscribe : undefined;
    }
  }, [evaluateFlag]);

  return value;
}

// -- Phase 1.7 composite hook (platform-specific, not shared) ---------------

export interface Phase17FeatureFlags {
  googleSignInEnabled: boolean;
  emailCaptureEnabled: boolean;
  signupVariant: SignupFlowVariant;
  isLoading: boolean;
}

/**
 * Web-specific feature flags hook (composite)
 *
 * Waits for PostHog to initialize via script snippet, then fetches flags.
 * Falls back to defaults after 15 seconds if PostHog fails to load.
 *
 * This hook is platform-specific because loading/timeout semantics differ
 * between native SDK and browser snippet.
 */
export function useFeatureFlags(): Phase17FeatureFlags {
  const [flags, setFlags] = useState<Phase17FeatureFlags>({
    googleSignInEnabled: false,
    emailCaptureEnabled: false,
    signupVariant: 'control',
    isLoading: true,
  });

  useEffect(() => {
    const checkPostHog = setInterval(() => {
      if (typeof window !== 'undefined' && window.posthog) {
        const isInitialized = (window.posthog as any).__initialized ||
                             (window.posthog as any).__loaded ||
                             typeof window.posthog.get_distinct_id === 'function';

        if (isInitialized) {
          clearInterval(checkPostHog);

          try {
            const googleSignInFlag = window.posthog.isFeatureEnabled('google_signin_enabled');
            const emailCaptureFlag = window.posthog.isFeatureEnabled('early_email_capture_enabled');
            const signupVariantFlag = window.posthog.getFeatureFlag('signup_flow_variant');

            setFlags({
              googleSignInEnabled: googleSignInFlag === true,
              emailCaptureEnabled: emailCaptureFlag === true,
              signupVariant: signupVariantFlag === 'optimized' ? 'optimized' : 'control',
              isLoading: false,
            });

            // feature_flags_evaluated retired (#797 -- PostHog tracks FF natively)
          } catch (error) {
            console.error('[FeatureFlags] Error loading flags:', error);
            setFlags(prev => ({ ...prev, isLoading: false }));
          }
        }
      }
    }, 100) as unknown as number;

    const defaults: Phase17FeatureFlags = {
      googleSignInEnabled: false,
      emailCaptureEnabled: false,
      signupVariant: 'control',
      isLoading: false,
    };
    const timeout = setTimeout(() => {
      clearInterval(checkPostHog);
      setFlags(prev => prev.isLoading ? defaults : prev);
      if (__DEV__) {
        console.warn('[FeatureFlags] PostHog initialization timeout after 15s, using defaults');
      }
    }, 15000) as unknown as number;

    return () => {
      clearInterval(checkPostHog);
      clearTimeout(timeout);
    };
  }, []);

  return flags;
}

// -- 27 composed hooks from shared factory ----------------------------------

// drama_plus_checkout_enabled PostHog flag removed (2026-03-18).
// Replaced with build-time CHECKOUT_ENABLED constant in subscription.tsx.
// See NocoDB task #1451.

const hooks = createFeatureFlagHooks(useFeatureFlag);

export const {
  useGoogleSignInFlag,
  useEmailCaptureFlag,
  useSignupVariantFlag,
  useV8LetterThrowingFlag,
  useL3AccordionsFlag,
  usePatternSpotlightFlag,
  useTransformationInsightFlag,
  useSharedLinksFlag,
  useGroupAnalysisFlag,
  useHistoryPatternsFlag,
  useMultimodalFlag,
  useGenerativeResultsFlag,
  useOnboardingFlowFlag,
  useReEngagementWhatsNewFlag,
  useDramaPlusConversionUxFlag,
  useStreamingResultsFlag,
  useSharingEnabledFlag,
  useOgImageGenerationFlag,
  useSelfHealingValidationFlag,
  useSduiRenderingFlag,
  useRewritesEnabledFlag,
  useNarratorDualVoiceFlag,
  useCrisisAssistanceFlag,
  usePractitionerPortalFlag,
  usePractitionerAccessVisibleFlag,
  useTurnstileEnabledFlag,
  useFamilyPlansFlag,
  useTrialPromptsFlag,
  useAiDetectionFlag,
  useStyleConsistencyFlag,
  useHabitEngineFlag,
  useDramaCardsFlag,
  useReferralEnabledFlag,
} = hooks;
